import { useEffect, useRef } from "react";

/**
 * Debounces a save function and triggers it automatically after `delay` ms
 * whenever `hasChanges` is true. Resets the timer on each render where
 * `hasChanges` is still true (e.g. additional edits before the timeout fires).
 * Clears the timer on unmount.
 *
 * @param {Function} saveFn     - the function to call after the delay
 * @param {boolean}  hasChanges - whether there are unsaved changes
 * @param {number}   [delay=4000] - debounce delay in milliseconds
 */
const useAutoSave = (saveFn, hasChanges, delay = 4000) => {
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (!hasChanges) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            saveFn();
        }, delay);

        return () => clearTimeout(timeoutRef.current);
    }, [saveFn, hasChanges, delay]);
};

export default useAutoSave;
